import { Skeleton } from "@mui/material";
import {
  Button,
  CircularProgress,
  Tooltip,
  Typography,
  useBreakpoint,
} from "@avenue-8/sidekick-ui";
import { Swiper, SwiperSlide } from "swiper/react";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RadarIcon from "@mui/icons-material/Radar";
import { FormattedDate } from "@shared/components/FormattedDate";
import { getContactRadarSignalsPath } from "@shared/utils/contact-briefing-links";
import { useNavigate } from "react-router-dom";
import {
  radarSourceEnum,
  type RadarSourceKey,
} from "@modules/radar/radar.types";
import {
  RESEARCH_FAILED_FALLBACK_MESSAGE,
  toMomentCardViewModel,
  type MomentCardViewModel,
} from "@modules/radar/shared/helpers/toMomentCardViewModel";
import { useSocialSignals, MAX_SIGNALS } from "./useSocialSignals";
import { useCountContacts } from "@shared/generated/radar/queries/contactsController/useCountContacts";
import { countContactsQueryResponseSchema } from "@shared/generated/radar/zod/contactsController/countContactsSchema";
import { useGoogleContactsAccess } from "@features/Contacts/hooks/useGoogleContactsAccess";
import { ContactsEmptyState } from "@features/Contacts/components/ContactsEmptyState";
import { useAddContactDialogStore } from "@store/add-contact-dialog.store";
import {
  ContactName,
  MiniBottomRow,
  MiniFailedIcon,
  MiniHeadline,
  MiniInsight,
  MiniStatusBlock,
  MiniTopRow,
  PanelEmptyState,
  PanelEmptyStateIconWrap,
  ScoreBadge,
  ScoreBadgeText,
  ScrollRow,
  SignalCardContent,
  SkeletonCard,
  SourceIconWrapperLarge,
  SpotlightCard,
  SpotlightCardActionArea,
  SwiperRow,
  TimeText,
  ViewAllLink,
  ViewContactHint,
} from "./SocialSignalsSection.styles";

const sourceIcons: Record<RadarSourceKey, React.ReactNode> = {
  [radarSourceEnum.INSTAGRAM]: <InstagramIcon fontSize="inherit" />,
  [radarSourceEnum.LINKEDIN]: <LinkedInIcon fontSize="inherit" />,
  [radarSourceEnum.FACEBOOK]: <FacebookIcon fontSize="inherit" />,
  [radarSourceEnum.WEB_SEARCH]: <TravelExploreIcon fontSize="inherit" />,
  [radarSourceEnum.GMAIL]: <EmailOutlinedIcon fontSize="inherit" />,
};

export const MOBILE_SKELETON_COUNT = 2;

type EmptyStateContent = {
  icon: React.ReactNode;
  title: string;
  description: string;
  ctaLabel?: string;
};

// Zero contacts at all gets the full ContactsEmptyState (Google consent +
// manual add) instead - this only covers "you have contacts, but Radar has
// nothing to show yet" in its two flavors (nothing monitored vs monitored
// but quiet).
const getEmptyStateContent = (isForbidden: boolean): EmptyStateContent => {
  if (isForbidden) {
    return {
      icon: <RadarIcon fontSize="small" />,
      title: "Setting up Radar",
      description: "Turn on monitoring for a contact from your Contacts list.",
      ctaLabel: "Go to Contacts",
    };
  }
  return {
    icon: <RadarIcon fontSize="small" />,
    title: "No relationship moments yet",
    description: "As Radar picks up new signals, they'll show up here.",
  };
};

const renderSignalCardBody = (
  item: MomentCardViewModel,
  onCardClick?: () => void,
) => (
  <SpotlightCardActionArea disabled={!onCardClick} onClick={onCardClick}>
    <SignalCardContent>
      <MiniTopRow>
        {item.source && (
          <Tooltip title={item.source}>
            <SourceIconWrapperLarge>
              {sourceIcons[item.source]}
            </SourceIconWrapperLarge>
          </Tooltip>
        )}
        <ContactName variant="subtitle2">{item.contactName}</ContactName>
        {item.score !== null && (
          <Tooltip title="Relevance score">
            <ScoreBadge $compact>
              <ScoreBadgeText data-compact variant="caption">
                {item.score}
              </ScoreBadgeText>
            </ScoreBadge>
          </Tooltip>
        )}
      </MiniTopRow>
      {item.isResearching ? (
        <MiniStatusBlock>
          <CircularProgress size={20} />
          <Typography variant="body2">Researching...</Typography>
        </MiniStatusBlock>
      ) : item.hasFailed ? (
        <MiniStatusBlock>
          <MiniFailedIcon>
            <ErrorOutlineIcon fontSize="small" />
          </MiniFailedIcon>
          <Typography variant="body2">
            {item.errorMessage ?? RESEARCH_FAILED_FALLBACK_MESSAGE}
          </Typography>
        </MiniStatusBlock>
      ) : (
        <>
          <MiniHeadline variant="subtitle2">{item.headline}</MiniHeadline>
          {item.insight && (
            <MiniInsight variant="body2" color="text.primary">
              {item.insight}
            </MiniInsight>
          )}
        </>
      )}
    </SignalCardContent>
    <MiniBottomRow>
      {item.occurredAt && (
        <TimeText variant="caption">
          <FormattedDate value={item.occurredAt} relative placement="top" />
        </TimeText>
      )}
      {onCardClick && (
        <ViewContactHint variant="body2" data-view-contact-hint>
          Suggested approach
          <ArrowForwardIcon fontSize="inherit" />
        </ViewContactHint>
      )}
    </MiniBottomRow>
  </SpotlightCardActionArea>
);

export const SocialSignalsSection = () => {
  const navigate = useNavigate();
  const isLaptopDown = useBreakpoint("laptop");
  const signalsQuery = useSocialSignals();
  const countContactsQuery = useCountContacts();
  const parsedCount = countContactsQueryResponseSchema.safeParse(
    countContactsQuery.data,
  );
  const hasNoContacts = parsedCount.success && parsedCount.data.count === 0;
  const { isConnected: isGoogleConnected, hasAccess: hasGoogleAccess } =
    useGoogleContactsAccess();
  const openAddContactDialog = useAddContactDialogStore(
    (s) => s.openAddContactDialog,
  );

  const goToContact = (contactId: string) =>
    navigate(getContactRadarSignalsPath(contactId));

  const items = (signalsQuery.data?.items ?? []).map(toMomentCardViewModel);
  const isForbidden =
    signalsQuery.isError && signalsQuery.error?.status === 403;
  const isEmpty = isForbidden || (!signalsQuery.isError && items.length === 0);
  const emptyStateContent =
    isEmpty && !hasNoContacts ? getEmptyStateContent(isForbidden) : null;

  return (
    <>
      {signalsQuery.isLoading ? (
        isLaptopDown ? (
          <SwiperRow>
            <Swiper
              spaceBetween={16}
              slidesPerView={1.25}
              observer={true}
              observeParents={true}
              resizeObserver={true}
              updateOnWindowResize={true}
            >
              {Array.from({ length: MOBILE_SKELETON_COUNT }).map((_, i) => (
                <SwiperSlide key={i}>
                  <SkeletonCard $inSwiper>
                    <Skeleton variant="rounded" width="100%" height={320} />
                  </SkeletonCard>
                </SwiperSlide>
              ))}
            </Swiper>
          </SwiperRow>
        ) : (
          <ScrollRow>
            {Array.from({ length: MAX_SIGNALS }).map((_, i) => (
              <SkeletonCard key={i}>
                <Skeleton variant="rounded" width="100%" height={220} />
              </SkeletonCard>
            ))}
          </ScrollRow>
        )
      ) : isEmpty && hasNoContacts ? (
        <ContactsEmptyState
          orientation="horizontal"
          hasRadarModule
          isGoogleConnected={isGoogleConnected}
          hasGoogleAccess={hasGoogleAccess}
          onAddContact={() => openAddContactDialog("manual")}
          onImportFromCsv={() => openAddContactDialog("csv")}
        />
      ) : emptyStateContent ? (
        <PanelEmptyState>
          <PanelEmptyStateIconWrap>
            {emptyStateContent.icon}
          </PanelEmptyStateIconWrap>
          <Typography variant="subtitle2">{emptyStateContent.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {emptyStateContent.description}
          </Typography>
          {emptyStateContent.ctaLabel && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/contacts")}
              sx={{ marginTop: "0.5rem" }}
            >
              {emptyStateContent.ctaLabel}
            </Button>
          )}
        </PanelEmptyState>
      ) : signalsQuery.isError ? (
        <PanelEmptyState>
          <PanelEmptyStateIconWrap>
            <ErrorOutlineIcon fontSize="small" />
          </PanelEmptyStateIconWrap>
          <Typography variant="subtitle2">Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary">
            We couldn&apos;t load your signals. Try refreshing the page.
          </Typography>
        </PanelEmptyState>
      ) : isLaptopDown ? (
        <SwiperRow>
          <Swiper spaceBetween={16} slidesPerView={1.25}>
            {items.map((item) => {
              const { contactId } = item;
              return (
                <SwiperSlide key={item.id}>
                  <SpotlightCard $inSwiper>
                    {renderSignalCardBody(
                      item,
                      contactId ? () => goToContact(contactId) : undefined,
                    )}
                  </SpotlightCard>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </SwiperRow>
      ) : (
        <ScrollRow>
          {items.map((item) => {
            const { contactId } = item;
            return (
              <SpotlightCard key={item.id}>
                {renderSignalCardBody(
                  item,
                  contactId ? () => goToContact(contactId) : undefined,
                )}
              </SpotlightCard>
            );
          })}
        </ScrollRow>
      )}
      {items.length > 0 && (
        <ViewAllLink to="/signals">
          View all social signals
          <ArrowForwardIcon fontSize="inherit" />
        </ViewAllLink>
      )}
    </>
  );
};
